package songwritersnotebook.app.screens

import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.automirrored.filled.HelpOutline
import androidx.compose.material.icons.automirrored.filled.Logout
import androidx.compose.material.icons.filled.*
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.foundation.background
import songwritersnotebook.app.billing.SubscriptionTier
import songwritersnotebook.app.viewmodels.MainViewModel
import songwritersnotebook.app.viewmodels.SettingsViewModel
import songwritersnotebook.app.utils.PlatformToast
import songwritersnotebook.app.utils.PlatformUtils
import songwritersnotebook.app.data.PublicUser
import songwritersnotebook.app.utils.Constants.APP_VERSION
import kotlinx.coroutines.launch
import songwritersnotebook.app.tutorial.TutorialManager
import songwritersnotebook.app.tutorial.TutorialStep
import songwritersnotebook.app.tutorial.tutorialTarget

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun SettingsScreen(
    onBack: () -> Unit,
    settingsViewModel: SettingsViewModel,
    mainViewModel: MainViewModel,
    onNavigateToSubscription: () -> Unit,
    onNavigateToRoadmap: () -> Unit,
    onNavigateToPrivacyPolicy: () -> Unit,
    onSignIn: () -> Unit,
    onNavigateToHome: () -> Unit
) {
    val settingsState by settingsViewModel.settings.collectAsState()
    val currentUser by mainViewModel.currentUser.collectAsState()
    val activeSubscription by mainViewModel.activeSubscription.collectAsState()
    val userProfile by mainViewModel.userProfile.collectAsState()
    var showDeleteDialog by remember { mutableStateOf(false) }
    var showEditUsernameDialog by remember { mutableStateOf(false) }
    var showDeviceManagementDialog by remember { mutableStateOf(false) }
    var showBlockedUsersDialog by remember { mutableStateOf(false) }
    var safetyTab by remember { mutableStateOf(0) } // 0 = Blocked, 1 = Contacts
    var showFeedbackDialog by remember { mutableStateOf(false) }
    var notificationsExpanded by remember { mutableStateOf(false) }
    var deleteErrorMessage by remember { mutableStateOf<String?>(null) }
    val platformUtils = remember { PlatformUtils() }
    val isWeb = remember { songwritersnotebook.app.getPlatform().name.contains("Web", ignoreCase = true) }
    
    val isRepairing by settingsViewModel.isRepairing.collectAsState()
    val repairProgress by settingsViewModel.repairProgress.collectAsState()
    
    val isSyncing by mainViewModel.isSyncing.collectAsState()
    
    val currentStep by TutorialManager.currentStep.collectAsState()

    val notificationPermissionLauncher = songwritersnotebook.app.utils.rememberNotificationPermissionLauncher(
        onGranted = { settingsViewModel.toggleDailyReminder() },
        onDenied = { PlatformToast.show("Permission denied. Reminders cannot be enabled.") }
    )

    val scrollState = rememberScrollState()
    val scope = rememberCoroutineScope()

    LaunchedEffect(Unit) {
        TutorialManager.registerAction(TutorialStep.SETTINGS_BACK) {
            onNavigateToHome()
        }
        TutorialManager.registerEnterAction(TutorialStep.HOME_SETTINGS_BUTTON) {
            onNavigateToHome()
        }
        TutorialManager.registerEnterAction(TutorialStep.SETTINGS_ACCOUNT) {
            scope.launch {
                // Scroll to the bottom or roughly where account is. 
                // Since it's a simple column, scrolling to max usually works if it's near bottom.
                scrollState.animateScrollTo(scrollState.maxValue)
            }
        }
        println("DEBUG: SettingsScreen - ActiveSub: $activeSubscription")
        if (userProfile == null) println("DEBUG: SettingsScreen - UserProfile is NULL")
        userProfile?.let { println("DEBUG: SettingsScreen - UserProfile Tier: ${it.subscriptionTier}, Quota: ${it.dataStorageQuotaBytes}") }
    }

    Scaffold(
        topBar = {
            TopAppBar(
                title = { Text("Settings", fontWeight = FontWeight.Bold) },
                navigationIcon = {
                    IconButton(
                        onClick = {
                            if (currentStep == TutorialStep.SETTINGS_BACK) {
                                TutorialManager.nextStep()
                                onNavigateToHome()
                            } else {
                                onBack()
                            }
                        },
                        modifier = Modifier.tutorialTarget(TutorialStep.SETTINGS_BACK)
                    ) {
                        Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = "Go back")
                    }
                }
            )
        }
    ) { paddingValues ->
        Column(modifier = Modifier
            .padding(paddingValues)
            .padding(16.dp)
            .fillMaxSize()
            .verticalScroll(rememberScrollState())
        ) {
            Text("GENERAL", style = MaterialTheme.typography.labelSmall, color = MaterialTheme.colorScheme.primary, modifier = Modifier.padding(bottom = 8.dp))
            Card(
                shape = RoundedCornerShape(8.dp),
                colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
                modifier = Modifier.tutorialTarget(TutorialStep.SETTINGS_GENERAL)
            ) {
                Row(modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 8.dp), verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.SpaceBetween) {
                    Text("Dark Mode")
                    Switch(checked = settingsState.isDarkMode, onCheckedChange = { settingsViewModel.toggleDarkMode() })
                }
                HorizontalDivider()
                Row(modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 8.dp), verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.SpaceBetween) {
                    Column(modifier = Modifier.weight(1f)) {
                        Text("Left Handed Mode")
                        Text("Flips the guitar fretboard for left-handed players.", style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
                    }
                    Switch(checked = settingsState.isLeftHanded, onCheckedChange = { settingsViewModel.toggleLeftHandedMode() })
                }
                HorizontalDivider()
                Row(modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 8.dp), verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.SpaceBetween) {
                    Text("Text Size")
                    Button(onClick = { settingsViewModel.cycleTextSize() }) {
                        Text(settingsState.textSize.displayName)
                    }
                }
            }
            
            Spacer(modifier = Modifier.height(24.dp))
            Text("NOTIFICATIONS", style = MaterialTheme.typography.labelSmall, color = MaterialTheme.colorScheme.primary, modifier = Modifier.padding(bottom = 8.dp))
            Card(
                shape = RoundedCornerShape(8.dp),
                colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface)
            ) {
                SettingsButton(
                    text = "Manage Notifications",
                    icon = if (notificationsExpanded) Icons.Default.ExpandLess else Icons.Default.ExpandMore,
                    onClick = { notificationsExpanded = !notificationsExpanded }
                )
                if (notificationsExpanded) {
                    HorizontalDivider()
                    Row(modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 8.dp), verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.SpaceBetween) {
                        Text("Daily Reminders")
                        Switch(
                            checked = settingsState.dailyReminderEnabled, 
                            onCheckedChange = { isEnabled ->
                                if (isEnabled) {
                                    notificationPermissionLauncher()
                                } else {
                                    settingsViewModel.toggleDailyReminder()
                                }
                            }
                        )
                    }
                    HorizontalDivider()
                    Row(modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 8.dp), verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.SpaceBetween) {
                        Text("News & Updates")
                        Switch(
                            checked = settingsState.newsAndUpdatesEnabled, 
                            onCheckedChange = { settingsViewModel.toggleNewsAndUpdates() }
                        )
                    }
                }
            }
            
            Spacer(modifier = Modifier.height(24.dp))
            Text("SUPPORT", style = MaterialTheme.typography.labelSmall, color = MaterialTheme.colorScheme.primary, modifier = Modifier.padding(bottom = 8.dp))
            Card(
                shape = RoundedCornerShape(8.dp),
                colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface)
            ) {
                SettingsButton(
                    text = "Show Tutorial",
                    icon = Icons.AutoMirrored.Filled.HelpOutline,
                    onClick = {
                        mainViewModel.requestTutorial()
                        onNavigateToHome()
                    }
                )
                HorizontalDivider()
                if (currentUser != null) {
                    SettingsButton(
                        text = "Quick Feedback",
                        icon = Icons.Default.ChatBubble,
                        onClick = { showFeedbackDialog = true }
                    )
                    HorizontalDivider()
                }
                SettingsButton(
                    text = "Email Support",
                    icon = Icons.Default.Email,
                    onClick = {
                        platformUtils.sendEmail(
                            to = "support@songwritersnotebook.app",
                            subject = "Songwriter's Notebook Support Request",
                            body = ""
                        )
                    }
                )
                HorizontalDivider()
                SettingsButton(
                    text = "Rate on Play Store",
                    icon = Icons.Default.Star,
                    onClick = {
                        platformUtils.openUrl("https://play.google.com/store/apps/details?id=songwritersnotebook.app")
                    }
                )
                 HorizontalDivider()
                SettingsButton(
                    text = "Product Roadmap",
                    icon = Icons.Default.Timeline,
                    onClick = onNavigateToRoadmap
                )
                HorizontalDivider()
                SettingsButton(
                    text = "Privacy Policy",
                    icon = Icons.Default.Info,
                    onClick = onNavigateToPrivacyPolicy
                )
            }
            
            Spacer(modifier = Modifier.height(24.dp))
            Text("ACCOUNT", style = MaterialTheme.typography.labelSmall, color = MaterialTheme.colorScheme.primary, modifier = Modifier.padding(bottom = 8.dp))
            Card(
                shape = RoundedCornerShape(8.dp), 
                colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
                modifier = Modifier.tutorialTarget(TutorialStep.SETTINGS_ACCOUNT)
            ) {
                if (currentUser == null) {
                    SettingsButton(text = "Sign in with Google", icon = Icons.Default.AccountCircle, onClick = onSignIn)
                } else {
                    // GDPR: Show pending deletion notice
                    if (userProfile?.deletionStatus == "pending") {
                        Column(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(16.dp)
                                .clip(RoundedCornerShape(8.dp))
                                .background(MaterialTheme.colorScheme.errorContainer)
                                .padding(16.dp)
                        ) {
                            Text(
                                "ACCOUNT DELETION PENDING",
                                style = MaterialTheme.typography.titleMedium,
                                color = MaterialTheme.colorScheme.onErrorContainer,
                                fontWeight = FontWeight.Bold
                            )
                            Spacer(Modifier.height(4.dp))
                            Text(
                                "Your account is scheduled for permanent deletion. All data will be scrubbed 30 days after your initial request.",
                                style = MaterialTheme.typography.bodyMedium,
                                color = MaterialTheme.colorScheme.onErrorContainer
                            )
                            Spacer(Modifier.height(12.dp))
                            Button(
                                onClick = {
                                    mainViewModel.cancelAccountDeletion(
                                        onSuccess = { PlatformToast.show("Deletion cancelled.") },
                                        onError = { PlatformToast.show(it) }
                                    )
                                },
                                colors = ButtonDefaults.buttonColors(containerColor = MaterialTheme.colorScheme.error)
                            ) {
                                Text("Cancel Deletion Request")
                            }
                        }
                        HorizontalDivider()
                    }

                    Row(modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 20.dp), verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.SpaceBetween) {
                        Text("Signed in as:", style = MaterialTheme.typography.bodyMedium)
                        Text(currentUser?.email ?: "No Email", style = MaterialTheme.typography.bodyMedium, fontWeight = FontWeight.Bold)
                    }
                    HorizontalDivider()
                    
                    // Username
                    SettingsButton(
                        text = "Username: ${userProfile?.username ?: "Set Username"}",
                        icon = Icons.Default.Person,
                        onClick = { showEditUsernameDialog = true }
                    )
                    HorizontalDivider()

                    SettingsButton(
                        text = "Manage Devices (${userProfile?.activeSessions?.size ?: 0}/3)",
                        icon = Icons.Default.Devices,
                        onClick = { showDeviceManagementDialog = true }
                    )
                    HorizontalDivider()

                    SettingsButton(
                        text = "Blocked Users (${userProfile?.blockedUsers?.size ?: 0})",
                        icon = Icons.Default.Block,
                        onClick = { showBlockedUsersDialog = true }
                    )
                    HorizontalDivider()

                    val subText = when (activeSubscription) {
                        SubscriptionTier.STUDIO -> "Studio"
                        SubscriptionTier.HOBBYIST -> "Hobbyist"
                        else -> "Free"
                    }
                    SettingsButton(text = "Subscription: $subText", icon = Icons.Default.WorkspacePremium, onClick = onNavigateToSubscription)
                    SettingsButton(text = "Sign Out", icon = Icons.AutoMirrored.Filled.Logout, onClick = { mainViewModel.signOut() })
                    HorizontalDivider()
                    TextButton(onClick = { showDeleteDialog = true }, modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(0.dp)) {
                        Row(modifier = Modifier.fillMaxWidth().padding(horizontal = 8.dp, vertical = 8.dp), verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.Start) {
                            Icon(Icons.Default.Delete, contentDescription = "Delete Account", tint = MaterialTheme.colorScheme.error)
                            Spacer(modifier = Modifier.width(16.dp))
                            Text("Delete Account", color = MaterialTheme.colorScheme.error)
                        }
                    }
                }
            }

            if (currentUser != null) {
                Spacer(modifier = Modifier.height(16.dp))
                if (activeSubscription == SubscriptionTier.STUDIO) {
                    userProfile?.let { profile ->
                        println("DEBUG_UI: SettingsScreen rendering storage - Used: ${profile.dataStorageUsedBytes}")
                        var isRecalculating by remember { mutableStateOf(false) }
                        val scope = rememberCoroutineScope()
                        
                        StorageUsageCard(
                            title = "Cloud Storage",
                            icon = Icons.Default.Cloud,
                            usedBytes = profile.dataStorageUsedBytes.toLong(),
                            quotaBytes = profile.dataStorageQuotaBytes.toLong(),
                            headerAction = {
                                if (isRecalculating) {
                                    CircularProgressIndicator(modifier = Modifier.size(24.dp), strokeWidth = 2.dp)
                                } else {
                                    TextButton(onClick = {
                                        if (currentUser != null) {
                                            isRecalculating = true
                                            scope.launch {
                                                try {
                                                                                                                                            settingsViewModel.recalculateStorage(currentUser!!.uid)
                                                                                                                                            mainViewModel.refreshUserProfile()
                                                                                                                                            PlatformToast.show("Storage updated!")
                                                                                                                                        } catch (e: Exception) {
                                                                                                                                            val msg = e.message ?: "Error updating storage."
                                                                                                                                            // Strip "functions/" prefix if present
                                                                                                                                            val cleanMsg = msg.replace("functions/resource-exhausted: ", "")
                                                                                                                                            PlatformToast.show(cleanMsg)
                                                                                                                                        }
                                                                                                                                        isRecalculating = false
                                            }
                                        } else {
                                            PlatformToast.show("You are not signed in.")
                                        }
                                    }) {
                                        Text("Refresh")
                                    }
                                }
                            }
                        )
                    }
                } else {
                    Card(shape = RoundedCornerShape(8.dp), colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface), modifier = Modifier.fillMaxWidth()) {
                        Column(modifier = Modifier.padding(horizontal = 16.dp, vertical = 12.dp)) {
                            Row(verticalAlignment = Alignment.CenterVertically) {
                                Icon(imageVector = Icons.Default.Cloud, contentDescription = "Cloud Storage", modifier = Modifier.size(24.dp))
                                Spacer(modifier = Modifier.width(8.dp))
                                Text(text = "Cloud Storage", style = MaterialTheme.typography.titleMedium)
                            }
                            Spacer(Modifier.height(12.dp))
                            Text(text = "Your subscription plan does not include cloud backup. All songs are saved locally to this device.", style = MaterialTheme.typography.bodyMedium, color = MaterialTheme.colorScheme.onSurfaceVariant)
                            TextButton(onClick = onNavigateToSubscription, modifier = Modifier.align(Alignment.End)) {
                                Text("Upgrade to Enable Backup")
                            }
                        }
                    }
                }

                val showMaintenance = activeSubscription == SubscriptionTier.STUDIO || !isWeb
                if (showMaintenance) {
                    Spacer(modifier = Modifier.height(24.dp))
                    Text("MAINTENANCE", style = MaterialTheme.typography.labelSmall, color = MaterialTheme.colorScheme.primary, modifier = Modifier.padding(bottom = 8.dp))
                    Card(shape = RoundedCornerShape(8.dp), colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface), modifier = Modifier.fillMaxWidth()) {
                        Column(modifier = Modifier.padding(vertical = 8.dp)) {
                            if (activeSubscription == SubscriptionTier.STUDIO) {
                                if (!isWeb) {
                                    Row(modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 8.dp), verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.SpaceBetween) {
                                        Column(modifier = Modifier.weight(1f).padding(end = 16.dp)) {
                                            Text("Download All Audio")
                                            Text(text = if (settingsState.downloadAllAudio) "All audio files will be kept on this device." else "Audio files download on-demand.", style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
                                        }
                                        Switch(checked = settingsState.downloadAllAudio, onCheckedChange = { settingsViewModel.toggleDownloadAllAudio() })
                                    }
                                    HorizontalDivider()
                                    SettingsButton(text = "Delete Local Audio Copies", icon = Icons.Default.Delete, onClick = { settingsViewModel.clearAudioCache() })
                                    HorizontalDivider()
                                }
                                
                                if (isSyncing) {
                                    Row(modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 12.dp), verticalAlignment = Alignment.CenterVertically) {
                                        CircularProgressIndicator(modifier = Modifier.size(24.dp), strokeWidth = 2.dp)
                                        Spacer(modifier = Modifier.width(16.dp))
                                        Text("Resyncing with Cloud...", style = MaterialTheme.typography.bodyMedium)
                                    }
                                } else {
                                    SettingsButton(
                                        text = "Resync Cloud Files", 
                                        icon = Icons.Default.CloudSync, 
                                        onClick = { 
                                            settingsViewModel.resyncAllData(mainViewModel)
                                            PlatformToast.show("Starting full cloud resync. This may take a minute...")
                                        }
                                    )
                                }
                                HorizontalDivider()
                                
                                SettingsButton(
                                    text = "Scan for Lost Recordings", 
                                    icon = Icons.Default.Search, 
                                    onClick = { 
                                        settingsViewModel.scanForOrphans { count ->
                                            PlatformToast.show("Recovery complete. Found $count files.")
                                        }
                                    }, 
                                    enabled = !isRepairing
                                )
                                HorizontalDivider()
                            }
                            
                            if (!isWeb) {
                                SettingsButton(text = "Repair Instrument Files", icon = Icons.Default.Build, onClick = { settingsViewModel.repairInstrumentCache() }, enabled = !isRepairing)
                                if (isRepairing) {
                                    Row(modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 8.dp), verticalAlignment = Alignment.CenterVertically) {
                                        CircularProgressIndicator(modifier = Modifier.size(20.dp), strokeWidth = 2.dp)
                                        Spacer(modifier = Modifier.width(16.dp))
                                        Text(repairProgress, style = MaterialTheme.typography.bodySmall)
                                    }
                                }
                            }
                        }
                    }
                }

                if (!isWeb) {
                    Spacer(modifier = Modifier.height(24.dp))
                    Text("CONNECT", style = MaterialTheme.typography.labelSmall, color = MaterialTheme.colorScheme.primary, modifier = Modifier.padding(bottom = 8.dp))
                    Card(shape = RoundedCornerShape(8.dp), colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface), modifier = Modifier.fillMaxWidth()) {
                        SettingsButton(
                            text = "Visit Website",
                            icon = Icons.Default.Public,
                            onClick = { platformUtils.openUrl("https://songwritersnotebook.app") }
                        )
                    }
                }
            }

            Spacer(modifier = Modifier.height(32.dp))
            Text(
                text = "Version $APP_VERSION",
                style = MaterialTheme.typography.labelMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.5f),
                modifier = Modifier.fillMaxWidth().padding(bottom = 16.dp),
                textAlign = androidx.compose.ui.text.style.TextAlign.Center
            )

            Spacer(modifier = Modifier.height(80.dp)) // Room for WebFooter
        }
    }

    if (showBlockedUsersDialog) {
        val blockedUsers = userProfile?.blockedUsers ?: emptyMap()
        val contacts by mainViewModel.contactCandidates.collectAsState(initial = emptyList())
        
        var userToBlockFromSafety by remember { mutableStateOf<PublicUser?>(null) }
        var userToReportFromSafety by remember { mutableStateOf<PublicUser?>(null) }

        AlertDialog(
            onDismissRequest = { showBlockedUsersDialog = false },
            title = { Text("Privacy & Safety") },
            text = {
                Column(modifier = Modifier.fillMaxWidth()) {
                    TabRow(selectedTabIndex = safetyTab) {
                        Tab(selected = safetyTab == 0, onClick = { safetyTab = 0 }) {
                            Text("Blocked", modifier = Modifier.padding(12.dp))
                        }
                        Tab(selected = safetyTab == 1, onClick = { safetyTab = 1 }) {
                            Text("Recent Contacts", modifier = Modifier.padding(12.dp))
                        }
                    }
                    
                    Spacer(modifier = Modifier.height(16.dp))
                    
                    Box(modifier = Modifier.heightIn(max = 300.dp)) {
                        if (safetyTab == 0) {
                            // BLOCKED LIST
                            if (blockedUsers.isEmpty()) {
                                Text("You haven't blocked any users.", modifier = Modifier.padding(16.dp))
                            } else {
                                Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                                    blockedUsers.forEach { entry ->
                                        val uid = entry.key
                                        val name = entry.value
                                        Card(
                                            modifier = Modifier.fillMaxWidth(),
                                            colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surfaceVariant)
                                        ) {
                                            Row(
                                                modifier = Modifier.padding(12.dp),
                                                verticalAlignment = Alignment.CenterVertically,
                                                horizontalArrangement = Arrangement.SpaceBetween
                                            ) {
                                                Column(modifier = Modifier.weight(1f)) {
                                                    Text(text = name, style = MaterialTheme.typography.bodyLarge, fontWeight = FontWeight.Bold)
                                                    Text(text = "ID: ...${uid.takeLast(6)}", style = MaterialTheme.typography.bodySmall)
                                                }
                                                TextButton(
                                                    onClick = { mainViewModel.unblockUser(uid) },
                                                    colors = ButtonDefaults.textButtonColors(contentColor = MaterialTheme.colorScheme.primary)
                                                ) {
                                                    Text("Unblock")
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        } else {
                            // CONTACTS LIST (CANDIDATES FOR BLOCKING)
                            val nonBlockedContacts = contacts.filter { !blockedUsers.containsKey(it.uid) }
                            
                            if (nonBlockedContacts.isEmpty()) {
                                Text("No recent collaborators or invitations found.", modifier = Modifier.padding(16.dp))
                            } else {
                                Column(verticalArrangement = Arrangement.spacedBy(12.dp), modifier = Modifier.verticalScroll(rememberScrollState())) {
                                    nonBlockedContacts.forEach { contactUser ->
                                        Card(
                                            modifier = Modifier.fillMaxWidth(),
                                            colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surfaceVariant)
                                        ) {
                                            Row(
                                                modifier = Modifier.padding(12.dp),
                                                verticalAlignment = Alignment.CenterVertically
                                            ) {
                                                Column(modifier = Modifier.weight(1f)) {
                                                    Text(text = contactUser.username, style = MaterialTheme.typography.bodyLarge, fontWeight = FontWeight.Bold)
                                                    Text(text = "ID: ...${contactUser.uid.takeLast(6)}", style = MaterialTheme.typography.bodySmall)
                                                }
                                                IconButton(onClick = { userToReportFromSafety = contactUser }) {
                                                    Icon(Icons.Default.Warning, "Report")
                                                }
                                                IconButton(onClick = { userToBlockFromSafety = contactUser }) {
                                                    Icon(Icons.Default.Block, "Block", tint = MaterialTheme.colorScheme.error)
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            },
            confirmButton = {
                TextButton(onClick = { showBlockedUsersDialog = false }) {
                    Text("Done")
                }
            }
        )

        // Sub-Confirmation Dialogs
        if (userToBlockFromSafety != null) {
            AlertDialog(
                onDismissRequest = { userToBlockFromSafety = null },
                title = { Text("Block ${userToBlockFromSafety!!.username}?") },
                text = { Text("This will remove them from all your songs and prevent them from sending you future invites.") },
                confirmButton = {
                    TextButton(onClick = {
                        mainViewModel.blockUser(userToBlockFromSafety!!.uid, userToBlockFromSafety!!.username)
                        userToBlockFromSafety = null
                    }) {
                        Text("Block", color = MaterialTheme.colorScheme.error)
                    }
                },
                dismissButton = {
                    TextButton(onClick = { userToBlockFromSafety = null }) {
                        Text("Cancel")
                    }
                }
            )
        }

        if (userToReportFromSafety != null) {
            AlertDialog(
                onDismissRequest = { userToReportFromSafety = null },
                title = { Text("Report ${userToReportFromSafety!!.username}?") },
                text = { Text("Prepare an abuse report for our moderation team? You will be redirected to your email app.") },
                confirmButton = {
                    TextButton(onClick = {
                        mainViewModel.reportUser(userToReportFromSafety!!.uid, userToReportFromSafety!!.username)
                        userToReportFromSafety = null
                    }) {
                        Text("Report")
                    }
                },
                dismissButton = {
                    TextButton(onClick = { userToReportFromSafety = null }) {
                        Text("Cancel")
                    }
                }
            )
        }
    }

    if (showDeviceManagementDialog) {
        val sessions = userProfile?.activeSessions?.values?.toList() ?: emptyList()
        val currentDeviceId = mainViewModel.deviceId

        AlertDialog(
            onDismissRequest = { showDeviceManagementDialog = false },
            title = { Text("Active Devices") },
            text = {
                Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                    Text("You can have up to 3 active devices on your account.", style = MaterialTheme.typography.bodySmall)
                    
                    sessions.forEach { session ->
                        val isCurrent = session.deviceId == currentDeviceId
                        Card(
                            modifier = Modifier.fillMaxWidth(),
                            colors = CardDefaults.cardColors(
                                containerColor = if (isCurrent) MaterialTheme.colorScheme.primaryContainer else MaterialTheme.colorScheme.surfaceVariant
                            )
                        ) {
                            Row(
                                modifier = Modifier.padding(12.dp),
                                verticalAlignment = Alignment.CenterVertically,
                                horizontalArrangement = Arrangement.SpaceBetween
                            ) {
                                Column(modifier = Modifier.weight(1f)) {
                                    Text(
                                        text = if (isCurrent) "${session.deviceName} (Current)" else session.deviceName,
                                        style = MaterialTheme.typography.bodyLarge,
                                        fontWeight = FontWeight.Bold
                                    )
                                    Text(text = session.platform, style = MaterialTheme.typography.bodySmall)
                                }
                                if (!isCurrent) {
                                    IconButton(onClick = { 
                                        scope.launch {
                                            mainViewModel.removeSession(session.deviceId)
                                        }
                                    }) {
                                        Icon(Icons.Default.Delete, contentDescription = "Remove Device", tint = MaterialTheme.colorScheme.error)
                                    }
                                }
                            }
                        }
                    }
                }
            },
            confirmButton = {
                TextButton(onClick = { showDeviceManagementDialog = false }) {
                    Text("Close")
                }
            }
        )
    }

    if (showEditUsernameDialog) {
        var newUsername by remember { mutableStateOf(userProfile?.username ?: "") }
        var isUpdating by remember { mutableStateOf(false) }
        var errorMsg by remember { mutableStateOf<String?>(null) }

        AlertDialog(
            onDismissRequest = { if (!isUpdating) showEditUsernameDialog = false },
            title = { Text("Update Username") },
            text = {
                Column {
                    Text("Choose a unique username for sharing and collaboration.")
                    Spacer(modifier = Modifier.height(16.dp))
                    OutlinedTextField(
                        value = newUsername,
                        onValueChange = { input ->
                            // Strictly enforce allowed characters (a-z, A-Z, 0-9, _) and no spaces/@
                            val filtered = input.filter { it.isLetterOrDigit() || it == '_' }
                            newUsername = filtered
                            errorMsg = null
                        },
                        label = { Text("Username") },
                        singleLine = true,
                        isError = errorMsg != null,
                        modifier = Modifier.fillMaxWidth()
                    )
                    if (errorMsg != null) {
                        Text(
                            text = errorMsg!!,
                            color = MaterialTheme.colorScheme.error,
                            style = MaterialTheme.typography.bodySmall,
                            modifier = Modifier.padding(top = 4.dp)
                        )
                    }
                }
            },
            confirmButton = {
                Button(
                    onClick = {
                        if (newUsername.isNotBlank()) {
                            isUpdating = true
                            mainViewModel.updateUsername(newUsername) { success, msg ->
                                isUpdating = false
                                if (success) {
                                    showEditUsernameDialog = false
                                    PlatformToast.show("Username updated!")
                                } else {
                                    errorMsg = msg
                                }
                            }
                        }
                    },
                    enabled = !isUpdating
                ) {
                    if (isUpdating) {
                        CircularProgressIndicator(modifier = Modifier.size(24.dp), strokeWidth = 2.dp)
                    } else {
                        Text("Save")
                    }
                }
            },
            dismissButton = {
                if (!isUpdating) {
                    TextButton(onClick = { showEditUsernameDialog = false }) {
                        Text("Cancel")
                    }
                }
            }
        )
    }
    
    if (showFeedbackDialog) {
        var feedbackMessage by remember { mutableStateOf("") }
        var isSending by remember { mutableStateOf(false) }

        AlertDialog(
            onDismissRequest = { if (!isSending) showFeedbackDialog = false },
            icon = { Icon(Icons.Default.Email, contentDescription = null) },
            title = { Text("Send Feedback") },
            text = {
                Column {
                    Text("We'd love to hear your thoughts, bugs, or feature requests!")
                    Spacer(modifier = Modifier.height(16.dp))
                    OutlinedTextField(
                        value = feedbackMessage,
                        onValueChange = { feedbackMessage = it },
                        label = { Text("Message") },
                        modifier = Modifier.fillMaxWidth(),
                        minLines = 3,
                        maxLines = 5
                    )
                }
            },
            confirmButton = {
                Button(
                    onClick = {
                        if (feedbackMessage.isNotBlank()) {
                            isSending = true
                            settingsViewModel.sendFeedback(feedbackMessage) { success ->
                                isSending = false
                                if (success) {
                                    showFeedbackDialog = false
                                    PlatformToast.show("Feedback sent!")
                                } else {
                                    PlatformToast.show("Failed to send.")
                                }
                            }
                        } else {
                            PlatformToast.show("Please enter a message.")
                        }
                    },
                    enabled = !isSending
                ) {
                    if (isSending) {
                        CircularProgressIndicator(modifier = Modifier.size(24.dp), strokeWidth = 2.dp)
                    } else {
                        Text("Send")
                    }
                }
            },
            dismissButton = {
                if (!isSending) {
                    TextButton(onClick = { showFeedbackDialog = false }) {
                        Text("Cancel")
                    }
                }
            }
        )
    }

    if (showDeleteDialog) {
        AlertDialog(
            onDismissRequest = { showDeleteDialog = false },
            icon = { Icon(Icons.Default.Warning, contentDescription = null, tint = MaterialTheme.colorScheme.error) },
            title = { Text("Delete Account?") },
            text = {
                Text("Your account and all associated data will be permanently deleted after a 30-day grace period. During this time, you can log in and cancel the request if you change your mind.")
            },
            confirmButton = {
                Button(
                    onClick = {
                        showDeleteDialog = false
                        mainViewModel.requestAccountDeletion(
                            onSuccess = {
                                PlatformToast.show("Deletion requested. You have 30 days to cancel.")
                            },
                            onError = { errorMessage ->
                                deleteErrorMessage = errorMessage
                            }
                        )
                    },
                    colors = ButtonDefaults.buttonColors(containerColor = MaterialTheme.colorScheme.error)
                ) {
                    Text("Request Deletion")
                }
            },
            dismissButton = {
                TextButton(onClick = { showDeleteDialog = false }) {
                    Text("Cancel")
                }
            }
        )
    }
    if (deleteErrorMessage != null) {
        AlertDialog(
            onDismissRequest = { deleteErrorMessage = null },
            icon = {
                Icon(Icons.Default.Error, contentDescription = "Error", tint = MaterialTheme.colorScheme.error)
            },
            title = { Text("Action Failed") },
            text = {
                Text(deleteErrorMessage ?: "Unknown error")
            },
            confirmButton = {
                Button(
                    onClick = {
                        deleteErrorMessage = null
                    }
                ) {
                    Text("OK")
                }
            }
        )
    }
}

@Composable
private fun SettingsButton(text: String, icon: ImageVector, onClick: () -> Unit, enabled: Boolean = true) {
    TextButton(onClick = onClick, modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(0.dp), enabled = enabled) {
        Row(modifier = Modifier.fillMaxWidth().padding(horizontal = 8.dp, vertical = 8.dp), verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.Start) {
            Icon(icon, contentDescription = text, tint = MaterialTheme.colorScheme.primary)
            Spacer(modifier = Modifier.width(16.dp))
            Text(text)
        }
    }
}

private fun formatStorage(bytes: Long): String {
    if (bytes <= 0) return "0 B"
    val units = listOf("B", "KB", "MB", "GB", "TB")
    var value = bytes.toDouble()
    var unitIndex = 0
    
    while (value >= 1024 && unitIndex < units.lastIndex) {
        value /= 1024
        unitIndex++
    }
    
    val rounded = (value * 10).toInt() / 10.0
    return "$rounded ${units[unitIndex]}"
}

@Composable
fun StorageUsageCard(title: String, icon: ImageVector?, usedBytes: Long, quotaBytes: Long, headerAction: @Composable (() -> Unit)? = null) {
    val progress = if (quotaBytes > 0) (usedBytes.toFloat() / quotaBytes.toFloat()).coerceIn(0f, 1f) else 0f
    val usedText = formatStorage(usedBytes)
    val quotaText = formatStorage(quotaBytes)

    Card(shape = RoundedCornerShape(8.dp), colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface), modifier = Modifier.fillMaxWidth()) {
        Column(modifier = Modifier.padding(16.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    if (icon != null) {
                        Icon(imageVector = icon, contentDescription = title, modifier = Modifier.size(24.dp))
                        Spacer(modifier = Modifier.width(8.dp))
                    }
                    Text(text = title, style = MaterialTheme.typography.titleMedium)
                }
                if (headerAction != null) headerAction()
            }
            Spacer(Modifier.height(16.dp))
            LinearProgressIndicator(progress = { progress }, modifier = Modifier.fillMaxWidth().height(8.dp).clip(CircleShape))
            Spacer(Modifier.height(8.dp))
            Row(horizontalArrangement = Arrangement.SpaceBetween, modifier = Modifier.fillMaxWidth()) {
                Text(text = "$usedText used", style = MaterialTheme.typography.bodySmall, fontWeight = FontWeight.Bold)
                Text(text = "$quotaText total", style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
            }
        }
    }
}
